import type { TilePoint, VirtualRoomKind } from '../../../../../../../../types/Maps';

/**
 * The map editor's local drafts of a map's VRs, in IndexedDB. Cloud Code holds the real rows
 * (SC-422); this store keeps the editor's working copy, keyed by `mapId`, so unsynced edits
 * survive a reload. Version 4 dropped the old per-`customRoomId` stores and the `SpawnPoint`
 * drafts: spawn points now live in the VR's `metadata.spawnPoint`.
 */
export interface VirtualRoom {
  id: string;
  name: string;
  color: string;
  createdAt: number;
  updatedAt: number;
  isActive: boolean;
  mapId: string;
  /** `meeting` when unset. `reception` is set from the Maps view, not the editor. */
  kind?: VirtualRoomKind;
  /** Has a change Cloud Code hasn't saved yet. A clean draft always takes the database's copy. */
  dirty?: boolean;
  properties: {
    uniqueRoomId: string;
    proximityDistance: number;
  };
  bounds?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  metadata?: {
    description?: string;
    capacity?: number;
    /** Where arrivals land, in tiles, just outside the VR. */
    spawnPoint?: TilePoint;
    [key: string]: unknown;
  };
  outlineData?: {
    tiles: string[]; // Array of "x,y" coordinates
    outlineTiles: string[]; // Array of "x,y" coordinates for outline
    lastUpdated: number;
  };
}

export class VirtualRoomStorage {
  private db: IDBDatabase | null = null;
  private readonly DB_NAME = 'VirtualRoomManager';
  private readonly STORE_NAME = 'rooms';
  private readonly VERSION = 4;
  private isInitialized = false;
  private currentMapId = '';

  setMapId(mapId: string): void {
    this.currentMapId = mapId;
  }

  getCurrentMapId(): string {
    return this.currentMapId;
  }

  async init(): Promise<void> {
    if (this.isInitialized) return;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, this.VERSION);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.isInitialized = true;
        resolve();
      };

      request.onupgradeneeded = () => {
        const db = request.result;
        // Drafts from before SC-422 are keyed by room, not map; the database has the real VRs.
        for (const name of Array.from(db.objectStoreNames)) {
          db.deleteObjectStore(name);
        }
        const store = db.createObjectStore(this.STORE_NAME, { keyPath: 'id' });
        store.createIndex('mapId', 'mapId', { unique: false });
      };
    });
  }

  private store(mode: IDBTransactionMode): IDBObjectStore {
    if (!this.db) throw new Error('Database not initialized');
    return this.db.transaction([this.STORE_NAME], mode).objectStore(this.STORE_NAME);
  }

  private request<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async createRoom(room: VirtualRoom): Promise<void> {
    const roomWithDefaults: VirtualRoom = {
      ...room,
      mapId: room.mapId || this.currentMapId,
      properties: {
        uniqueRoomId: room.properties?.uniqueRoomId || room.id,
        proximityDistance: room.properties?.proximityDistance || 5,
        ...room.properties
      }
    };
    await this.request(this.store('readwrite').add(roomWithDefaults));
  }

  /** The current map's drafts, newest first. */
  async getRooms(): Promise<VirtualRoom[]> {
    const rooms = await this.request(this.store('readonly').index('mapId').getAll(this.currentMapId));
    return (rooms as VirtualRoom[]).sort((a, b) => b.createdAt - a.createdAt);
  }

  async getRoomById(roomId: string): Promise<VirtualRoom | null> {
    return ((await this.request(this.store('readonly').get(roomId))) as VirtualRoom | undefined) || null;
  }

  /** Creates the draft when it doesn't exist, so a database row can be mirrored in one call. */
  async putRoom(room: VirtualRoom): Promise<void> {
    await this.request(this.store('readwrite').put(room));
  }

  async updateRoom(roomId: string, updates: Partial<VirtualRoom>): Promise<void> {
    const existing = await this.getRoomById(roomId);
    if (!existing) throw new Error(`Room with id ${roomId} not found`);
    await this.putRoom({ ...existing, ...updates, updatedAt: Date.now() });
  }

  async deleteRoom(roomId: string): Promise<void> {
    await this.request(this.store('readwrite').delete(roomId));
  }

  /** Marks one of the current map's drafts active; `isActive` is editor state, never saved. */
  async setActiveRoom(roomId: string): Promise<void> {
    const rooms = await this.getRooms();
    if (!rooms.some((room) => room.id === roomId)) {
      throw new Error(`Room ${roomId} is not on map ${this.currentMapId}`);
    }
    const store = this.store('readwrite');
    await Promise.all(
      rooms
        .filter((room) => room.isActive !== (room.id === roomId))
        .map((room) => this.request(store.put({ ...room, isActive: room.id === roomId, updatedAt: Date.now() })))
    );
  }

  async exportRooms(): Promise<string> {
    return JSON.stringify(await this.getRooms(), null, 2);
  }

  /** Imported drafts join the current map, whatever map they were exported from. */
  async importRooms(jsonData: string): Promise<VirtualRoom[]> {
    const rooms: VirtualRoom[] = JSON.parse(jsonData).map((room: VirtualRoom) => ({ ...room, mapId: this.currentMapId, dirty: true }));
    for (const room of rooms) {
      await this.putRoom(room);
    }
    return rooms;
  }
}
